import React, { useState } from "react";
import ChannelIcon from "./ChannelIcon";
import Tooltip from "./Tooltip";
import InfoIcon from "./InfoIcon";

const ChannelClarityTable = ({ data }) => {
  const [sortConfig, setSortConfig] = useState({
    key: 'score',
    direction: 'desc'
  });

  // Sort function
  const sortedData = [...data].sort((a, b) => {
    let aValue, bValue;
    
    if (sortConfig.key === 'clarityLevel') {
      // For clarity level, sort by the actual score value
      aValue = a.score;
      bValue = b.score;
    } else {
      aValue = a[sortConfig.key];
      bValue = b[sortConfig.key];
    }
    
    if (aValue < bValue) {
      return sortConfig.direction === 'asc' ? -1 : 1;
    }
    if (aValue > bValue) {
      return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  // Handle sorting when a column header is clicked
  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Get the appropriate sort direction indicator
  const getSortDirectionIndicator = (key) => {
    if (sortConfig.key !== key) {
      return null;
    }
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  // Function to determine score color
  const getScoreColor = (score) => {
    return 'text-black-0';
  };

  // Function to get metric color based on value (for complexity, questions, etc.)
  const getMetricColor = (value, isComplexity = false) => {
    if (isComplexity) {
      // For complexity, higher values are worse (red), lower values are better (green)
      if (value >= 70) return 'text-red-600';
      if (value >= 50) return 'text-yellow-600';
      return 'text-green-600';
    } else {
      // For questions and action items, moderate values are normal
      if (value >= 60) return 'text-blue-600';
      if (value >= 30) return 'text-black-0';
      return 'text-grey-60';
    }
  };

  // Function to get clarity level based on score
  const getClarityLevel = (score) => {
    if (score >= 80) return "Very clear";
    if (score >= 60) return "Clear";
    if (score >= 40) return "Moderate";
    if (score >= 20) return "Unclear";
    return "Very unclear";
  };

  // Function to get clarity level color for badge styling
  const getClarityLevelBadgeColor = (level) => {
    switch (level) {
      case "Very clear": return "bg-[#DAFBEB] text-black-0";
      case "Clear": return "bg-[#DAFBEB] text-black-0"; 
      case "Moderate": return "bg-[#FBF0DA] text-black-0";
      case "Unclear": return "bg-[#FBDADA] text-black-0";
      case "Very unclear": return "bg-[#FBDADA] text-black-0";
      default: return "bg-grey-20 text-grey-60";
    }
  };

  return (
    <div>
      <h3 className="text-md font-normal text-black-0 px-10 py-3">Channel clarity</h3>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-grey-20">
          <thead>
            <tr>
              <th 
                onClick={() => requestSort('channel')}
                className="pl-10 pr-5 py-2 text-left text-sm font-normal text-grey-60 cursor-pointer hover:text-black-0"
              >
                Channel {getSortDirectionIndicator('channel')}
              </th>
              <th 
                onClick={() => requestSort('score')}
                className="pl-10 pr-5 py-2 text-left text-sm font-normal text-grey-60 cursor-pointer hover:text-black-0"
              >
                <span className="inline-flex items-center gap-1">
                  Score {getSortDirectionIndicator('score')}
                  <Tooltip content="Overall clarity score (0–100)." placement="bottom" align="start">
                    <span className="inline-flex cursor-help">
                      <InfoIcon className="w-4 h-4" />
                    </span>
                  </Tooltip>
                </span>
              </th>
              <th 
                onClick={() => requestSort('complexity')}
                className="pl-10 pr-5 py-2 text-left text-sm font-normal text-grey-60 cursor-pointer hover:text-black-0"
              >
                <span className="inline-flex items-center gap-1">
                  Complexity {getSortDirectionIndicator('complexity')}
                  <Tooltip content="Estimated message complexity (higher can reduce clarity)." placement="bottom" align="start">
                    <span className="inline-flex cursor-help">
                      <InfoIcon className="w-4 h-4" />
                    </span>
                  </Tooltip>
                </span>
              </th>
              <th 
                onClick={() => requestSort('questionFrequency')}
                className="pl-10 pr-5 py-2 text-left text-sm font-normal text-grey-60 cursor-pointer hover:text-black-0"
              >
                <span className="inline-flex items-center gap-1">
                  Questions {getSortDirectionIndicator('questionFrequency')}
                  <Tooltip content="Share of messages that are questions." placement="bottom" align="start">
                    <span className="inline-flex cursor-help">
                      <InfoIcon className="w-4 h-4" />
                    </span>
                  </Tooltip>
                </span>
              </th>
              <th 
                onClick={() => requestSort('actionItemFrequency')}
                className="pl-10 pr-5 py-2 text-left text-sm font-normal text-grey-60 cursor-pointer hover:text-black-0"
              >
                <span className="inline-flex items-center gap-1">
                  Action items {getSortDirectionIndicator('actionItemFrequency')}
                  <Tooltip content="Share of messages that contain action items." placement="bottom" align="start">
                    <span className="inline-flex cursor-help">
                      <InfoIcon className="w-4 h-4" />
                    </span>
                  </Tooltip>
                </span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-grey-20">
            {sortedData.map((channel) => (
              <tr key={channel.id} className="hover:bg-white-f">
                <td className="pl-10 pr-5 py-3 text-sm font-medium text-black-0">
                  <div className="flex items-center gap-1">
                    <ChannelIcon isPrivate={channel.is_private} size={14} />
                    <span>{channel.channel}</span>
                  </div>
                </td>
                <td className="pl-10 pr-5 py-3 text-sm font-normal">
                  <span className={getScoreColor(channel.score)}>
                    {channel.score}%
                  </span>
                </td>
                <td className="pl-10 pr-5 py-3 text-sm font-normal">
                  <span className={getMetricColor(channel.complexity || 0, true)}>
                    {channel.complexity || 0}%
                  </span>
                </td>
                <td className="pl-10 pr-5 py-3 text-sm font-normal">
                  <span className={getMetricColor(channel.questionFrequency || 0)}>
                    {channel.questionFrequency || 0}%
                  </span>
                </td>
                <td className="pl-10 pr-5 py-3 text-sm font-normal">
                  <span className={getMetricColor(channel.actionItemFrequency || 0)}>
                    {channel.actionItemFrequency || 0}%
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ChannelClarityTable; 